import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import { Calendar, User, Folder, MessageSquare, FileText } from 'lucide-react';
import { Session, sessionApi } from '@/services/api';

interface SessionDetailsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  session: Session | null;
}

export function SessionDetailsModal({ open, onOpenChange, session }: SessionDetailsModalProps) {
  const [fullSession, setFullSession] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && session) {
      loadSessionDetails();
    }
  }, [open, session]);

  const loadSessionDetails = async () => {
    if (!session) return;

    setLoading(true);
    try {
      const details = await sessionApi.fetchSessionWithHistory(session.session_id);
      setFullSession(details);
    } catch (error) {
      console.error('Failed to load session details:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    const isoString = dateString.replace(' ', 'T') + 'Z';
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getMessageCount = () => {
    if (!fullSession?.chat_history) return 0;
    return Array.isArray(fullSession.chat_history) ? fullSession.chat_history.length : 0;
  };

  const getDocumentCount = () => {
    if (!fullSession?.session_context) return 0;
    const context = fullSession.session_context;
    let count = 0;

    if (context.current_patents) count += context.current_patents.length;
    if (context.current_publications) count += context.current_publications.length;
    if (context.current_applications) count += context.current_applications.length;
    if (context.current_drafts) count += context.current_drafts.length;
    if (context.cfr_sections) count += context.cfr_sections.length;
    if (context.mpep_sections) count += context.mpep_sections.length;
    if (context.fed_reg_sections) count += context.fed_reg_sections.length;
    if (context.usc_sections) count += context.usc_sections.length;
    if (context.pros_docs) count += context.pros_docs.length;
    if (context.resource_ids) count += context.resource_ids.length;

    return count;
  };

  const renderDocumentsList = () => {
    if (!fullSession?.session_context) return null;
    const context = fullSession.session_context;
    const documents: Array<{ type: string; items: string[] }> = [];

    if (context.current_patents?.length > 0) {
      documents.push({ type: 'Patents', items: context.current_patents });
    }
    if (context.current_publications?.length > 0) {
      documents.push({ type: 'Publications', items: context.current_publications });
    }
    if (context.current_applications?.length > 0) {
      documents.push({ type: 'Applications', items: context.current_applications });
    }
    if (context.current_drafts?.length > 0) {
      // For drafts, show the draft title if available, otherwise show draft_id
      const draftItems = context.current_drafts.map((draft: any) => {
        if (typeof draft === 'string') return draft;
        return draft.title || draft.draft_id || 'Untitled Draft';
      });
      documents.push({ type: 'Drafts', items: draftItems });
    }
    if (context.mpep_sections?.length > 0) {
      documents.push({ type: 'MPEP Sections', items: context.mpep_sections.map((s: string) => `MPEP ${s}`) });
    }
    if (context.cfr_sections?.length > 0) {
      documents.push({ type: 'CFR Sections', items: context.cfr_sections.map((s: string) => `37 CFR ${s}`) });
    }
    if (context.usc_sections?.length > 0) {
      documents.push({ type: 'USC Sections', items: context.usc_sections.map((s: string) => `USC ${s}`) });
    }
    if (context.fed_reg_sections?.length > 0) {
      documents.push({ type: 'Federal Register', items: context.fed_reg_sections });
    }
    if (context.resource_names?.length > 0) {
      documents.push({ type: 'Uploaded Files', items: context.resource_names });
    }

    return documents.map((doc, idx) => (
      <div key={idx} className="mb-3">
        <h4 className="text-sm font-medium text-gray-700 mb-1">{doc.type}</h4>
        <div className="flex flex-wrap gap-1">
          {doc.items.map((item, i) => (
            <Badge key={i} variant="outline" className="text-xs">
              {item}
            </Badge>
          ))}
        </div>
      </div>
    ));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>Session Details</DialogTitle>
          <DialogDescription>
            {session?.session_name}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="text-center py-8 text-gray-500">
            Loading session details...
          </div>
        ) : (
          <ScrollArea className="max-h-[60vh] pr-4">
            <div className="space-y-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wider">Session ID</label>
                  <p className="text-sm text-gray-900 font-mono mt-1">{session?.session_id}</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wider">Owner</label>
                  <div className="flex items-center gap-1 mt-1">
                    <User className="w-3 h-3 text-gray-400" />
                    <p className="text-sm text-gray-900">{session?.username}</p>
                  </div>
                </div>
              </div>

              {/* Timestamps */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wider">Created</label>
                  <div className="flex items-center gap-1 mt-1">
                    <Calendar className="w-3 h-3 text-gray-400" />
                    <p className="text-sm text-gray-900">{formatDate(session?.created || '')}</p>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wider">Last Modified</label>
                  <div className="flex items-center gap-1 mt-1">
                    <Calendar className="w-3 h-3 text-gray-400" />
                    <p className="text-sm text-gray-900">{formatDate(session?.last_modified || '')}</p>
                  </div>
                </div>
              </div>

              {/* Folder */}
              {session?.folder_name && (
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wider">Folder</label>
                  <div className="flex items-center gap-1 mt-1">
                    <Folder className="w-3 h-3 text-yellow-500" />
                    <p className="text-sm text-gray-900">{session.folder_name}</p>
                  </div>
                </div>
              )}

              {/* Stats */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                  <div className="flex items-center gap-2 mb-1">
                    <MessageSquare className="w-4 h-4 text-blue-600" />
                    <label className="text-xs text-blue-700 uppercase tracking-wider font-medium">Messages</label>
                  </div>
                  <p className="text-2xl font-semibold text-blue-900">{getMessageCount()}</p>
                </div>
                <div className="bg-green-50 rounded-lg p-3 border border-green-100">
                  <div className="flex items-center gap-2 mb-1">
                    <FileText className="w-4 h-4 text-green-600" />
                    <label className="text-xs text-green-700 uppercase tracking-wider font-medium">Documents</label>
                  </div>
                  <p className="text-2xl font-semibold text-green-900">{getDocumentCount()}</p>
                </div>
              </div>

              {/* Documents List */}
              {getDocumentCount() > 0 && (
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wider block mb-2">Active Documents</label>
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    {renderDocumentsList()}
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>
        )}

        <div className="flex justify-end pt-4 border-t">
          <Button onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
